import clsx from 'clsx';
import { Sticker } from '../sticker';

export function formatPrice(price: number) {
  const decimal = price % 1;
  const priceString = Math.floor(price)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (decimal > 0) {
    return (
      <span>
        {`${priceString}`},{Math.round(decimal * 100)}
      </span>
    );
  }
  return priceString;
}

export type PriceProps = {
  price?: number;
  priceText?: string;
  memberPrice?: number;
  campaignPrice?: number;
  memberPriceText?: string;
  normalPriceText?: string;
  campaignPriceText?: string;
  size?: 'lg' | 'md' | 'sm' | 'xs' | undefined;
  withStickers?: boolean;
  discountPercentOverride?: number;
  discountAmountOverride?: number;
  children?: any;
};

export function calculatePercentOff(before: number, after: number) {
  return Math.round(100 - (after / before) * 100);
}

export function calculateDiscount(before: number, after: number) {
  return before - after;
}

export interface PriceAmountProps {
  price: number;
  size: PriceProps['size'];
  className?: string;
}

export const PriceAmount = ({
  price,
  size,
  className = '',
}: PriceAmountProps) => {
  return (
    <span
      className={`
        block font-bold whitespace-nowrap leading-none
        ${size === 'sm' ? 'hd-lg' : ''}
        ${size === 'md' ? 'hd-xl' : ''}
        ${size === 'lg' ? 'hd-xl' : ''}
        ${className}
      `}
    >
      {formatPrice(price)}
    </span>
  );
};

interface PriceTitleProps {
  size: PriceProps['size'];
  children: React.ReactNode;
}

function PriceTitle({ size, children }: PriceTitleProps) {
  return (
    <span
      className={clsx('block font-medium text-xs whitespace-nowrap', {
        'mt-0 text-xxs': size === 'xs',
        'mt-2': size !== 'xs',
      })}
    >
      {children}
    </span>
  );
}

export function PriceBlackFirday({
  price,
  priceText,
  memberPrice,
  campaignPrice,
  memberPriceText = 'Medlemspris',
  normalPriceText = 'Ikke medlemspris',
  campaignPriceText = 'Før',
  discountPercentOverride,
  discountAmountOverride,
  withStickers = true,
  size = 'md',
  children,
}: PriceProps) {
  if (!price) {
    return null;
  }

  if (campaignPrice && price && campaignPrice !== price) {
    const discountPercent =
      discountPercentOverride || calculatePercentOff(price, campaignPrice);
    const discountAmount =
      discountAmountOverride || calculateDiscount(price, campaignPrice);

    return (
      <>
        <span className={clsx({ block: size === 'xs' })}>
          <span className="block text-promotion-base">
            <PriceTitle size={size}>Medlemspris</PriceTitle>
            <div className="flex flex-wrap gap-2 items-center">
              <PriceAmount
                price={campaignPrice}
                size={size}
                className="text-primary-50"
              />
              {memberPrice && (
                <PriceAmount
                  price={memberPrice}
                  size="sm"
                  className="text-neutral-70 line-through font-normal text-sm"
                />
              )}
            </div>
          </span>
          <span className="block text-xxs font-medium">
            Ikke medlemspris <span>{formatPrice(price)}</span>
          </span>
        </span>

        {size !== 'sm' && withStickers && (
          <div>
            <Sticker variant="neutralLight" size="sm" className="px-1">
              <strong>-{discountPercent}%</strong>
            </Sticker>
            <Sticker variant="neutralDark" size="sm" className="pr-2">
              Spar {formatPrice(discountAmount)}
            </Sticker>
          </div>
        )}
      </>
    );
  }

  if (memberPrice && price && memberPrice !== price) {
    const discountPercent =
      discountPercentOverride || calculatePercentOff(price, memberPrice);
    const discountAmount =
      discountAmountOverride || calculateDiscount(price, memberPrice);

    return (
      <div className={clsx('inline-block', { flex: size === 'xs' })}>
        <span className={clsx({ block: size === 'xs' })}>
          <PriceTitle size={size}>{memberPriceText}</PriceTitle>
          <PriceAmount
            price={memberPrice}
            size={size}
            className="block text-primary-50"
          />
          <div className="text-xxs">
            {normalPriceText} <span>{formatPrice(price)}</span>
          </div>
        </span>

        {size !== 'sm' && withStickers && (
          <div className="mt-2">
            <Sticker variant="neutralLight" size="sm">
              <strong className="font-medium">-{discountPercent}%</strong>
            </Sticker>
            <Sticker variant="neutralDark" size="sm" className="pr-3">
              Spar {formatPrice(discountAmount)}
            </Sticker>
          </div>
        )}
      </div>
    );
  }

  if (campaignPrice == price) {
    return (
      <div>
        <PriceTitle size={size}>{priceText}</PriceTitle>
        <PriceAmount price={price} size={size} className="text-primary-50" />
        <div className="mt-1">{children}</div>
      </div>
    );
  }

  return (
    <div>
      <PriceTitle size={size}>{priceText}</PriceTitle>
      <PriceAmount price={price} size={size} />
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default PriceBlackFirday;
